/**
 * Permission guard service for route protection
 * Used by RouteGuard and PermissionGuard components
 */

import { UserRole } from '../permissions/roles';
import { navigationService, UserPermissions } from '../navigation/navigationService';
import { OrgType, DEFAULT_ORG_TYPE } from '@/lib/types/org';
import { Logger } from '@/lib/logger';

export interface PermissionCheckOptions {
  mode: 'static' | 'database';
  orgType?: OrgType;
  fallbackToStatic?: boolean;
}

/**
 * Permission guard service for route and component protection
 */
export class PermissionGuardService {
  private static instance: PermissionGuardService;
  
  public static getInstance(): PermissionGuardService {
    if (!PermissionGuardService.instance) {
      PermissionGuardService.instance = new PermissionGuardService();
    }
    return PermissionGuardService.instance;
  }

  /**
   * Check if user has permission to access a route
   */
  public async checkRoutePermission(
    pathname: string,
    userRole: UserRole | string | null,
    options: PermissionCheckOptions,
    userPermissions?: UserPermissions[]
  ): Promise<boolean> {
    const requestId = Math.random().toString(36).substring(2, 12);
    
    Logger.info('PermissionGuardService', 'Starting route permission check', {
      pathname,
      userRole,
      mode: options.mode,
      orgType: options.orgType,
      requestId
    });

    // Parse user role if it's a string
    const parsedRole = typeof userRole === 'string' 
      ? navigationService.parseUserRole(userRole)
      : userRole;

    if (!parsedRole) {
      Logger.warn('PermissionGuardService', 'Invalid user role, denying access', {
        pathname,
        userRole,
        requestId
      });
      return false;
    }

    try {
      if (options.mode === 'static') {
        return this.checkStaticRoutePermission(pathname, parsedRole, options.orgType);
      } else {
        return await this.checkDatabaseRoutePermission(
          pathname, 
          userPermissions || [], 
          options.orgType,
          parsedRole,
          options.fallbackToStatic
        );
      }
    } catch (error) {
      Logger.apiError('PermissionGuardService', 'Error checking route permission', error as Error, {
        method: 'CHECK',
        action: 'checkRoutePermission',
        requestId,
        userId: parsedRole.toString(),
        error: error instanceof Error ? error.stack : String(error)
      });

      // Fallback to static if enabled
      if (options.mode === 'database' && options.fallbackToStatic) {
        Logger.warn('PermissionGuardService', 'Database check failed, falling back to static', {
          pathname,
          userRole: parsedRole,
          requestId
        });
        return this.checkStaticRoutePermission(pathname, parsedRole, options.orgType);
      }

      return false;
    }
  }

  /**
   * Check route permission using static role mapping
   */
  private checkStaticRoutePermission(
    pathname: string,
    userRole: UserRole,
    orgType: OrgType = DEFAULT_ORG_TYPE
  ): boolean {
    const requestId = Math.random().toString(36).substring(2, 12);
    
    Logger.info('PermissionGuardService', 'Checking static route permission', {
      pathname,
      userRole,
      orgType,
      requestId
    });

    const hasAccess = navigationService.hasStaticUrlPermission(userRole, pathname);
    
    Logger.info('PermissionGuardService', 'Static route permission check completed', {
      pathname,
      userRole,
      hasAccess,
      requestId
    });

    return hasAccess;
  }

  /**
   * Check route permission using database permissions
   */
  private async checkDatabaseRoutePermission(
    pathname: string,
    userPermissions: UserPermissions[],
    orgType: OrgType = DEFAULT_ORG_TYPE,
    userRole?: UserRole,
    fallbackToStatic: boolean = true
  ): Promise<boolean> {
    const requestId = Math.random().toString(36).substring(2, 12);
    
    Logger.info('PermissionGuardService', 'Checking database route permission', {
      pathname,
      userPermissionsCount: userPermissions.length,
      orgType,
      userRole,
      requestId
    });

    // If no permissions provided, try to fetch them
    if (!userPermissions || userPermissions.length === 0) {
      try {
        userPermissions = await this.fetchUserPermissions();
      } catch (error) {
        Logger.warn('PermissionGuardService', 'Failed to fetch user permissions', {
          pathname,
          error: error instanceof Error ? error.message : String(error),
          requestId
        });

        if (fallbackToStatic && userRole) {
          Logger.info('PermissionGuardService', 'Falling back to static permission check', {
            pathname,
            userRole,
            requestId
          });
          return this.checkStaticRoutePermission(pathname, userRole, orgType);
        }

        return false;
      }
    }

    const hasAccess = navigationService.hasDatabaseUrlPermission(userPermissions, pathname);
    
    Logger.info('PermissionGuardService', 'Database route permission check completed', {
      pathname,
      userPermissionsCount: userPermissions.length,
      hasAccess,
      requestId
    });

    return hasAccess;
  }

  /**
   * Fetch user permissions from API
   */
  private async fetchUserPermissions(): Promise<UserPermissions[]> {
    const response = await fetch('/api/misc/user-permissions', {
      method: 'GET',
      credentials: 'include'
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch user permissions: ${response.status} ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Check if user has access to a specific module
   */
  public checkModulePermission(
    moduleKey: string,
    userRole: UserRole | null,
    userPermissions?: UserPermissions[]
  ): boolean {
    const requestId = Math.random().toString(36).substring(2, 12);
    
    Logger.info('PermissionGuardService', 'Checking module permission', {
      moduleKey,
      userRole,
      userPermissionsCount: userPermissions?.length || 0,
      requestId
    });

    if (!userRole) {
      return false;
    }

    // Check static permissions if no database permissions provided
    if (!userPermissions || userPermissions.length === 0) {
      const allowedModules = require('../permissions/roleModulePermissions').getModuleKeysByRole(userRole);
      return allowedModules.includes(moduleKey);
    }

    // Check database permissions
    const hasAccess = userPermissions.some(perm => perm.modulekey === moduleKey && perm.canread);
    
    Logger.info('PermissionGuardService', 'Module permission check completed', {
      moduleKey,
      userRole,
      hasAccess,
      requestId
    });

    return hasAccess;
  }

  /**
   * Get navigation for user (wrapper method)
   */
  public async getNavigationForUser(
    userRole: UserRole | string | null,
    options: PermissionCheckOptions,
    userPermissions?: UserPermissions[]
  ) {
    // Parse user role if it's a string
    const parsedRole = typeof userRole === 'string' 
      ? navigationService.parseUserRole(userRole)
      : userRole;

    if (!parsedRole) {
      throw new Error('Invalid user role provided');
    }

    if (options.mode === 'static') {
      return navigationService.getStaticNavigation(parsedRole, options.orgType);
    } else {
      // Fetch permissions if not provided
      const permissions = userPermissions || await this.fetchUserPermissions();
      return navigationService.getDatabaseNavigation(permissions, options.orgType);
    }
  }
}

// Export singleton instance
export const permissionGuardService = PermissionGuardService.getInstance();
